import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, MessageCircle, Printer, Power } from "lucide-react";
import { toast } from "sonner";

const waLink = (phone, message) => {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  const withCc = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${withCc}?text=${encodeURIComponent(message)}`;
};

const inr = (v) => `₹${(Number(v) || 0).toLocaleString("en-IN")}`;

export default function StudentProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [homework, setHomework] = useState([]);
  const [sessionAtt, setSessionAtt] = useState([]);
  const [school, setSchool] = useState(null);

  const load = async () => {
    try {
      const [stu, l, h, a, sch] = await Promise.all([
        api.get(`/students/${id}`),
        api.get(`/fee/ledger/${id}`).catch(() => ({ data: null })),
        api.get(`/erp/homework`).catch(() => ({ data: [] })),
        api.get(`/erp/session-attendance?student_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/schools/mine`).catch(() => ({ data: null })),
      ]);
      setS(stu.data); setLedger(l.data); setHomework(h.data); setSessionAtt(a.data); setSchool(sch.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const toggleStatus = async () => {
    const newStatus = s.status === "inactive" ? "active" : "inactive";
    if (!window.confirm(`Mark ${s.first_name} as ${newStatus.toUpperCase()}?`)) return;
    try { await api.put(`/students/${s.id}/status`, { status: newStatus }); toast.success(`Marked ${newStatus}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendWaFee = () => {
    const phone = s?.parent_details?.father?.mobile || s?.parent_details?.mother?.mobile || s?.preferred_mob;
    if (!phone) { toast.error("No parent WhatsApp number"); return; }
    const schoolName = school?.name || "School";
    const due = ledger?.due || 0;
    const msg = `Dear Parent,\n\nWith due respect, this is a gentle reminder from ${schoolName}.\n\nOutstanding fee for ${s.first_name} ${s.last_name || ""} (Class ${s.class_name}${s.section ? " " + s.section : ""}, Reg: ${s.registration_no || "—"}) is *₹${due.toLocaleString("en-IN")}*.\n\nKindly clear the dues at your earliest convenience so that regular services remain uninterrupted.\n\nThank you.\n— ${schoolName} Fee Office`;
    const url = waLink(phone, msg);
    if (url) window.open(url, "_blank");
  };

  if (!s) return <DashLayout variant="school" title="Student Profile"><div className="text-slate-500 p-6">Loading…</div></DashLayout>;

  const total = sessionAtt.reduce((n, r) => n + (r.entries?.length || 0), 0);
  const present = sessionAtt.reduce((n, r) => n + (r.entries || []).filter(e => e.status === "present").length, 0);
  const pctOverall = total ? Math.round(present * 100 / total) : 0;
  const now = new Date();
  const week = sessionAtt.filter(r => (r.date || "") >= new Date(now.getTime() - 7 * 86400000).toISOString().slice(0, 10));
  const month = sessionAtt.filter(r => (r.date || "").startsWith(now.toISOString().slice(0, 7)));
  const year = sessionAtt.filter(r => (r.date || "").startsWith(String(now.getFullYear())));
  const pctOf = (list) => { const t = list.reduce((n, r) => n + (r.entries?.length || 0), 0); const p = list.reduce((n, r) => n + (r.entries || []).filter(e => e.status === "present").length, 0); return t ? Math.round(p * 100 / t) : 0; };

  return (
    <DashLayout variant="school" title={`${s.first_name} ${s.last_name || ""} — Student Profile`}>
      <div className="flex items-center gap-3 mb-4">
        <Button variant="outline" size="sm" onClick={() => nav("/school/students")} data-testid="sp-back"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <button onClick={toggleStatus} data-testid="sp-status-toggle" className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${s.status === "inactive" ? "border-red-300 text-red-700 bg-red-50" : s.status === "left" ? "border-slate-300 text-slate-600 bg-slate-100" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`}>
          <Power className="w-3 h-3 inline mr-1" />{(s.status || "active").toUpperCase()}
        </button>
        {(ledger?.due || 0) > 0 && (
          <Button size="sm" onClick={sendWaFee} className="bg-emerald-700 hover:bg-emerald-800 ml-auto" data-testid="sp-wa"><MessageCircle className="w-4 h-4 mr-1" />WhatsApp Reminder</Button>
        )}
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">Reg No</div><div className="font-bold">{s.registration_no || "—"}</div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">Class</div><div className="font-bold">{s.class_name} {s.section}</div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">DOB</div><div>{s.dob}</div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">Blood</div><div>{s.blood_group || "—"}</div></Card>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="sp-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="sp-tab-att">Attendance</TabsTrigger>
          <TabsTrigger value="fees" data-testid="sp-tab-fees">Fees</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="sp-tab-hw">Assignments</TabsTrigger>
          <TabsTrigger value="parent" data-testid="sp-tab-parent">Parent</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card className="p-6 space-y-2 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><b className="text-slate-500">Gender:</b> {s.gender}</div>
              <div><b className="text-slate-500">Board:</b> {s.board}</div>
              <div><b className="text-slate-500">Session:</b> {s.session || "—"}</div>
              <div><b className="text-slate-500">Status:</b> {s.status || "active"}</div>
              <div><b className="text-slate-500">Nationality:</b> {s.nationality || "—"}</div>
              <div><b className="text-slate-500">Religion:</b> {s.religion || "—"}</div>
              <div><b className="text-slate-500">Preferred Mob:</b> {s.preferred_mob || "—"}</div>
              <div><b className="text-slate-500">Transport:</b> {s.transport ? "Yes" : "No"}</div>
            </div>
            <div className="pt-3 border-t"><b className="text-slate-500">Permanent Address:</b> {s.address_details?.permanent || "—"}</div>
          </Card>
        </TabsContent>

        <TabsContent value="attendance">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">Weekly</div><div className="text-2xl font-black">{pctOf(week)}%</div><div className="text-[10px] text-slate-400">{week.length} sessions</div></Card>
            <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">Monthly</div><div className="text-2xl font-black">{pctOf(month)}%</div><div className="text-[10px] text-slate-400">{month.length} sessions</div></Card>
            <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">Yearly</div><div className="text-2xl font-black">{pctOf(year)}%</div><div className="text-[10px] text-slate-400">{year.length} sessions</div></Card>
            <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">Overall</div><div className="text-2xl font-black">{pctOverall}%</div><div className="text-[10px] text-slate-400">{present}/{total}</div></Card>
          </div>
          <Card className="p-4 max-h-96 overflow-y-auto space-y-1">
            {sessionAtt.length === 0 && <div className="text-xs text-slate-400 text-center py-6">No session attendance recorded</div>}
            {sessionAtt.map(r => {
              const e = (r.entries || [])[0];
              return (
                <div key={r.id} className="border rounded p-2 text-xs flex justify-between">
                  <div><b>{r.subject_name}</b> · {r.date} · P{r.period}</div>
                  <span className={`px-2 rounded-full ${e?.status === "absent" ? "bg-red-100 text-red-700" : e?.status === "late" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{e?.status || "—"}{e?.reason ? ` · ${e.reason}` : ""}</span>
                </div>
              );
            })}
          </Card>
        </TabsContent>

        <TabsContent value="fees">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">Billed</div><div className="text-xl font-bold tabular-nums">{inr(ledger?.total_fee)}</div></Card>
            <Card className="p-4 bg-amber-50 border-amber-200"><div className="text-[10px] uppercase text-amber-700">Discount</div><div className="text-xl font-bold tabular-nums text-amber-700">{inr(ledger?.discount)}</div></Card>
            <Card className="p-4 bg-emerald-50 border-emerald-200"><div className="text-[10px] uppercase text-emerald-700">Paid</div><div className="text-xl font-bold tabular-nums text-emerald-700">{inr(ledger?.paid)}</div></Card>
            <Card className="p-4 bg-red-50 border-red-200"><div className="text-[10px] uppercase text-red-700">Due</div><div className="text-xl font-bold tabular-nums text-red-700">{inr(ledger?.due)}</div></Card>
          </div>
          <Card className="p-4 max-h-96 overflow-y-auto space-y-1 text-xs">
            {(ledger?.ledger || []).length === 0 && <div className="text-slate-400 text-center py-6">No ledger entries</div>}
            {(ledger?.ledger || []).map((l, i) => (
              <div key={i} className="flex justify-between border-b pb-1">
                <div><span className="font-mono text-[10px]">{l.date}</span> · {l.description}</div>
                <div className={l.type === "payment" ? "text-emerald-700 font-semibold" : ""}>{l.type === "payment" ? `+${inr(l.paid)}` : inr(l.amount)}</div>
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="assignments">
          <Card className="p-4 max-h-96 overflow-y-auto space-y-1">
            {homework.length === 0 && <div className="text-xs text-slate-400 text-center py-6">No assignments</div>}
            {homework.map(h => {
              const overdue = new Date(h.due_date) < new Date();
              return (
                <div key={h.id} className="border rounded p-2 text-xs flex justify-between">
                  <div><b>{h.title}</b> — <span className="text-slate-500">Due {h.due_date}</span></div>
                  <span className={`px-2 rounded-full ${overdue ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{overdue ? "Overdue" : "Pending"}</span>
                </div>
              );
            })}
          </Card>
        </TabsContent>

        <TabsContent value="parent">
          <Card className="p-6 grid md:grid-cols-2 gap-4 text-sm">
            <div><b className="text-slate-500">Father:</b> {s.parent_details?.father?.name || "—"}</div>
            <div><b className="text-slate-500">Father Occupation:</b> {s.parent_details?.father?.occupation || "—"}</div>
            <div><b className="text-slate-500">Mother:</b> {s.parent_details?.mother?.name || "—"}</div>
            <div><b className="text-slate-500">Mother Occupation:</b> {s.parent_details?.mother?.occupation || "—"}</div>
            <div><b className="text-slate-500">Guardian:</b> {s.parent_details?.guardian?.name || "—"}</div>
            <div><b className="text-slate-500">Preferred Mob:</b> {s.preferred_mob || "—"}</div>
          </Card>
        </TabsContent>
      </Tabs>
    </DashLayout>
  );
}
